import {
  fromQuery,
  mapAsync,
  type Async,
  type QueryLike,
} from "@bfc-software/bfc-web-ui/lib/async-utils";
import { hasSearch, toRow } from "../../-data/task-model";
import type { TaskRow } from "../../-data/task-model";
import type { TasksRead } from "../../-data/use-tasks";
import type { TaskFilters } from "../../-data/filters";
import type { WirePagination } from "../../-data/wire";

/**
 * The All tasks tab, as state.
 *
 * One read and therefore one level of nesting, unlike the plans tab - but the
 * same three cases, from the same `fromQuery`, so the two tabs cannot drift
 * apart on what "failed" means or on whether a refetch counts as loading.
 *
 * Almost nothing is derived here. Every filter and the status chips are request
 * params (`use-tasks.ts`), so this is a mapping into rows rather than a model: no
 * predicate, no counting, and no scope of its own. The one thing it adds is what
 * the page says *about* its read - that a search has overridden the chips.
 */

export interface TasksPage {
  rows: TaskRow[];
  /** A search is on, so the chips do not describe what is drawn. */
  searchOverride: boolean;
  /** The envelope, straight through, because the pager reads all of it. */
  pagination: WirePagination;
  /**
   * How much matches the request in total, across every page.
   *
   * The activation listing counts, so this is the warehouse's answer rather than
   * the length of the page on screen - which is what lets the header state a
   * number that does not change when someone walks to page 2. Still nullable,
   * because absence on this envelope means "not reported" rather than zero.
   */
  totalCount: number | null;
}

export type TasksPageState = Async<TasksPage>;

export function tasksPageStateFrom(
  query: QueryLike<TasksRead>,
  filters: TaskFilters,
): TasksPageState {
  return mapAsync(fromQuery(query), (read) => ({
    // Straight through, in the order the service sent them. Nothing is filtered
    // on this side, so a short page is a short answer and not a hidden one.
    rows: read.page.tasks.map(toRow),
    searchOverride: hasSearch(filters),
    pagination: read.pagination,
    totalCount: read.page.totalCount,
  }));
}
